import axios from "axios";
import Cookies from "js-cookie";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { GlobalContext } from "./global-context";
import { errorMessage } from "../component/Util/errorMessage";
import { handleSessionRefusal } from "./session-refusal";

const AuthContext = createContext();

export default AuthContext;

export const AuthContextProvider = ({ children }) => {
  const globalCtx = useContext(GlobalContext);
  
  const [authTokens, setAuthTokens] = useState(
    localStorage.getItem("authtokens")
      ? JSON.parse(localStorage.getItem("authtokens"))
      : null
  );
  const [user, setUser] = useState(null);
  // RCMC-22 (ticket 10): the session's tokens as of now, null once it has ended. A ref kept in step with every
  // change, because a refusal can arrive before React re-renders: only the first refusal may end a session, and
  // only a refusal of its current access token.
  const session = useRef(authTokens);
  // RCMC-22: true from the moment a session expires until the sign-in screen has announced it. Kept here, not in
  // the URL, so it survives however the app gets to /login, and a reload never repeats it.
  const [sessionExpired, setSessionExpired] = useState(false);

  const saveTokens = (tokens) => {
    session.current = tokens;
    setAuthTokens(tokens);
    localStorage.setItem("authtokens", JSON.stringify(tokens));
  };

  const logoutUser = () => {
    session.current = null;
    setAuthTokens(null);
    setUser(null);
    localStorage.removeItem("authtokens");
    // The messages on screen belong to the session that ended (e.g. "Holding added … — <Company>"). v1.3 goes to
    // sign-in without reloading the page (ticket 10), so they would otherwise follow the Investor there.
    globalCtx.clearAlert();
    globalCtx.clearStatus();
  };

  // RCMC-22: the one expired-session handler. The API refused the session, or the token refresh failed: clear the
  // stored session (so the next person on the device cannot reuse it) and flag why. Every signed-in route sits under
  // LoadingComponent, which sends a cleared session to sign-in inside the app (v1.2's `window.location` reload
  // wiped any message); the sign-in screen then announces the expiry. The public Accessibility help page (ticket 12,
  // Util/PublicPageLayout) announces it itself and stays.
  // Session policy is unchanged (lifetimes, rotation and storage are the server's and v1.2's).
  const expireSession = () => {
    if (!session.current) return; // already ended (by Sign out, or an earlier refusal from the same session)
    logoutUser();
    setSessionExpired(true);
  };
  const expireSessionRef = useRef(expireSession);
  expireSessionRef.current = expireSession;

  // Every 401 / 403 to a request sent with a session token comes here (see session-refusal.js)
  useEffect(
    () =>
      handleSessionRefusal((token) => {
        // A token older than the current one (refreshed meanwhile): the session is alive, and the screen reports
        // the failure as it would any other
        if (session.current && token !== session.current.access) return false;
        expireSessionRef.current();
        return true;
      }),
    []
  );

  // Verify the sign-in OTP and start the session. v1.3 ticket 09 (RCMC-14): the sign-in screen passes the phone
  // number and password the OTP was sent for (kept from the first step, so Enter in the OTP field works too) and
  // handles a refusal itself — a wrong OTP goes on the OTP field — so this returns the request's promise: it
  // resolves once signed in and rejects with the request's error. (v1.2 read the fields from the clicked button's
  // form and raised the error here.) Same payload.
  const loginUser = ({ phone_no, password, otp }) =>
    axios
      .post("/api/user/otp/verify/login", { phone_no, password, otp }, {
        headers: { "X-CSRFToken": Cookies.get("csrftoken") },
      })
      .then((response) => {
        if (response.status === 200) {
          saveTokens(response.data.tokens);
          const user = response.data.user_details;
          setUser(user);
          setSessionExpired(false); // a new session: an expiry not yet announced is out of date
          return user;
        }
      });

  const updateToken = async () => {
    // Review of ticket 10: the reply only counts for the session it was sent for. Since Sign out no longer reloads
    // the page, a refresh in flight then would otherwise store fresh tokens after it (the next person on the device
    // signed straight back in); and an older refresh's failure must not end a newer session.
    const sentFor = session.current;
    const stillCurrent = () => session.current === sentFor;
    axios
      .post("/api/token/refresh", {
        refresh: authTokens?.refresh,
      })
      .then((response) => {
        if (!stillCurrent()) return;
        if (response.status === 200) {
            saveTokens(response.data);
        } else {
          expireSession();
        }
      })
      // RCMC-22: a refresh that fails ends the session (v1.2: the error text as a toast, then a silent logout)
      .catch(() => {
        if (stillCurrent()) expireSession();
      });
  };

  // Stable, so the sign-in screen's effect can list it as a dependency
  const clearSessionExpired = useCallback(() => setSessionExpired(false), []);

  const contextData = {
    loginUser: loginUser,
    user: user,
    logoutUser: logoutUser,
    authTokens: authTokens,
    updateToken:updateToken,
    // RCMC-22: the sign-in screen announces an expiry, then clears it
    sessionExpired: sessionExpired,
    clearSessionExpired: clearSessionExpired,
  };

  useEffect(() => {
    if (authTokens) {
        axios
        .get("/api/user/user-details", {
            headers: { Authorization: "Bearer " + authTokens.access },
        })
        .then((res) => {
            setUser(res.data.user_details);
        })
        .catch((e) => {
            // A refused session never gets here: the expired-session handler takes it (RCMC-22)
            globalCtx.raiseAlert(errorMessage(e), "error");
        });
    }
  },[])

  useEffect(() => {
    let fourMinutes = 1000 * 60 * 4;
    let interval = setInterval(() => {
      if (authTokens) {
        updateToken();
      }
    }, fourMinutes);
    return () => clearInterval(interval);
  }, [authTokens]);

  return (
    <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>
  );
};
